import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { EmptyState, Pager, Spinner, StatTile } from './Layout';
import JalaliDateInput from './JalaliDateInput';
import { errorMessage, stats as statsApi, users as usersApi } from '../utils/api';
import { formatNumber, formatToman } from '../utils/format';
import { formatJalali, jalaliMonthRange } from '../utils/jalali';
import { SELECTABLE_EDIT_TYPES } from '../utils/constants';

const PAGE_SIZE = 50;

/**
 * گزارش کارانه داخل سامانه — همان فیلترهای خروجی CSV (`fee_report_cases`).
 *
 * مبنای تاریخ **تأیید فنی** است، چون کارانه همان لحظه قفل می‌شود. پیش‌فرض ماه
 * شمسیِ جاری است؛ ماه‌های قبل با دکمه یا بازه‌ی دلخواه دیده می‌شوند.
 * مدیر همه‌ی ادیتورها را می‌بیند، ادیتور فقط کارنامه‌ی خودش را (سرور اعمال می‌کند).
 */
export default function FeeReport({ isAdmin = false }) {
  const thisMonth = jalaliMonthRange(0);
  const [range, setRange] = useState({ from: thisMonth.from, to: thisMonth.to, label: thisMonth.label });
  const [editorId, setEditorId] = useState('');
  const [editType, setEditType] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [editors, setEditors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // جست‌وجو با کمی تأخیر، تا برای هر حرف یک درخواست نرود.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!isAdmin) return;
    usersApi
      .list({ role: 'editor', page_size: 200 })
      .then(({ data: users }) => setEditors(users.results ?? []))
      .catch(() => {});
  }, [isAdmin]);

  const filters = useMemo(
    () => ({
      ...(range.from ? { from: range.from } : {}),
      ...(range.to ? { to: range.to } : {}),
      ...(isAdmin && editorId ? { editor: editorId } : {}),
      ...(editType ? { edit_type: editType } : {}),
      ...(query ? { q: query } : {}),
    }),
    [range.from, range.to, isAdmin, editorId, editType, query]
  );

  useEffect(() => setPage(1), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    statsApi
      .feeReport({ ...filters, page, page_size: PAGE_SIZE })
      .then(({ data: report }) => !cancelled && setData(report))
      .catch((err) => !cancelled && toast.error(errorMessage(err, 'گزارش کارانه بارگذاری نشد.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [filters, page]);

  const pickMonth = (offset) => {
    const month = jalaliMonthRange(offset);
    setRange({ from: month.from, to: month.to, label: month.label });
  };

  const setBound = (bound) => (value) =>
    setRange((prev) => ({ ...prev, [bound]: value, label: 'بازه‌ی دلخواه' }));

  const download = async () => {
    setBusy(true);
    try {
      await statsApi.downloadFeeReport(filters);
    } catch (err) {
      toast.error(errorMessage(err, 'خروجی گزارش کارانه گرفته نشد.'));
    } finally {
      setBusy(false);
    }
  };

  const lastMonth = jalaliMonthRange(-1);
  const pageCount = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <section className="panel">
      <h2 className="panel__title">
        گزارش کارانه — <bdi>{range.label}</bdi>
      </h2>
      <p className="form__note">
        بر اساس تاریخ تأیید فنی. کنتور هر ماه شمسی از صفر شروع می‌شود؛ ماه‌های قبل
        با دکمه‌ها یا بازه‌ی دلخواه در دسترس‌اند.
      </p>

      <div className="toolbar">
        <button
          type="button"
          className={`btn btn--sm ${range.label === thisMonth.label ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => pickMonth(0)}
        >
          {thisMonth.label}
        </button>
        <button
          type="button"
          className={`btn btn--sm ${range.label === lastMonth.label ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => pickMonth(-1)}
        >
          {lastMonth.label}
        </button>
        <JalaliDateInput label="از" value={range.from} onChange={setBound('from')} />
        <JalaliDateInput label="تا" value={range.to} onChange={setBound('to')} />
      </div>

      <div className="toolbar">
        {isAdmin && (
          <select className="input" value={editorId} onChange={(e) => setEditorId(e.target.value)} aria-label="ادیتور">
            <option value="">همه‌ی ادیتورها</option>
            {editors.map((editor) => (
              <option key={editor.id} value={editor.id}>
                {editor.display_name || editor.full_name || editor.username}
              </option>
            ))}
          </select>
        )}
        <select className="input" value={editType} onChange={(e) => setEditType(e.target.value)} aria-label="نوع پرونده">
          <option value="">همه‌ی انواع</option>
          {SELECTABLE_EDIT_TYPES.map((type) => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>
        <input
          className="input"
          type="search"
          placeholder="جست‌وجو: نام بیمار، پزشک یا شماره نسخه"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" className="btn btn--ghost" disabled={busy || !data?.count} onClick={download}>
          ⬇️ دانلود همین گزارش (CSV)
        </button>
      </div>

      {data && (
        <div className="tiles">
          <StatTile label="پرونده‌ی تأییدشده" value={formatNumber(data.totals.count)} />
          <StatTile label="مجموع کارانه" value={formatToman(data.totals.fee)} tone="success" />
        </div>
      )}

      {isAdmin && data?.per_editor?.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>ادیتور</th>
                <th>پرونده</th>
                <th>کارانه</th>
              </tr>
            </thead>
            <tbody>
              {data.per_editor.map((row) => (
                <tr key={row.editor_id ?? 'none'}>
                  <td><bdi>{row.editor_name || '—'}</bdi></td>
                  <td className="num">{formatNumber(row.count)}</td>
                  <td className="num">{formatToman(row.fee)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <Spinner />}

      {!loading && data && data.results.length === 0 && (
        <EmptyState icon="🧾" title="در این بازه پرونده‌ی تأییدشده‌ای نیست." />
      )}

      {!loading && data?.results?.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>تأیید فنی</th>
                <th>بیمار</th>
                <th>نوع</th>
                {isAdmin && <th>ادیتور</th>}
                <th>اصلاح</th>
                <th>کارانه</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((row) => (
                <tr key={row.id}>
                  <td className="num">{formatJalali(row.technical_approved_at)}</td>
                  <td>
                    <bdi>{row.name}</bdi>
                    <div className="muted">
                      <bdi>{row.doctor}</bdi> · <span className="num">{row.order_number}</span>
                    </div>
                  </td>
                  <td><bdi>{row.edit_type_name}</bdi></td>
                  {isAdmin && <td><bdi>{row.editor_name}</bdi></td>}
                  <td className="num">{formatNumber(row.revision_count)}</td>
                  <td className="num">{formatToman(row.fee)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager page={page} pageCount={pageCount} pageSize={PAGE_SIZE} count={data?.count ?? 0} onChange={setPage} />
    </section>
  );
}
